import { createContext, ReactNode, useEffect } from 'react';

import {
  TEmbeddedModeState,
  TEMApplication,
  TEMTenantState,
  TEMStateManager,
  EMApplicationStatuses,
  TMessage,
} from './embed-mode.types';
import { useEMStateManager } from './embed-mode-state-manager.hook';

export const EmbedModeContext = createContext<TEmbeddedModeState>({
  state: null,
  changeApplication: null,
  getApplicationById: null,
});

export const EmbedModeWithContext = (props: { children: ReactNode }) => {
  const {
    state: EMState,
    setState: setEMState,
    changeApplication,
    getApplicationById,
  }: TEMStateManager = useEMStateManager();

  const messageLisener = ({ data }: MessageEvent<TMessage>) => {
    if (typeof window.gwh.appGallery?.[data?.method] === 'function') {
      window.gwh.appGallery?.[data?.method](data?.payload);
    }
  };

  useEffect(() => {
    window.addEventListener('message', messageLisener);

    return () => {
      window.removeEventListener('message', messageLisener);
    };
  }, []);

  window.gwh.appGallery.startup = (state: TEMTenantState): void => {
    if (EMState) return;

    setEMState((prevState: TEmbeddedModeState) => {
      return { ...prevState, state };
    });
  };

  window.gwh.appGallery.getVersion = () => {
    window.parent.postMessage(
      {
        method: 'getVersion',
        payload: {
          appGalleryVersion: window.gwh.appGallery.version,
        },
      },
      '*'
    );
  };

  const setNewStatus = ({
    success,
    status,
  }: Pick<TEMApplication, 'success' | 'status'>): EMApplicationStatuses => {
    if (success === undefined) {
      return status;
    }

    if (success === false && status === EMApplicationStatuses['INSTALL']) {
      return EMApplicationStatuses['UNINSTALLED'];
    }

    if (success === false && status === EMApplicationStatuses['UNINSTALL']) {
      return EMApplicationStatuses['INSTALLED'];
    }

    if (success && status === EMApplicationStatuses['INSTALL']) {
      return EMApplicationStatuses['INSTALLED'];
    }

    if (success && status === EMApplicationStatuses['UNINSTALL']) {
      return EMApplicationStatuses['UNINSTALLED'];
    }

    return status;
  };

  window.gwh.appGallery.appStatusChanged = (
    application: TEMApplication
  ): void => {
    const status = setNewStatus({
      success: application.success,
      status: application.status,
    });
    const app = getApplicationById && getApplicationById(application?.appId);
    changeApplication &&
      app &&
      changeApplication({ ...app, status }, { silence: true });
  };

  window.gwh.appGallery.getApps = (): TEMApplication[] | any => {
    window.parent.postMessage(
      {
        method: 'getApps',
        payload: EMState?.apps,
      } || [],
      '*'
    );
    return EMState?.apps || [];
  };

  window.gwh.appGallery.loaded = true;
  window.gwh.appGallery.version = document.getElementById(
    'app-gallery-version'
  )?.innerText;

  return (
    <EmbedModeContext.Provider
      value={{ state: EMState, changeApplication, getApplicationById }}
    >
      {props.children}
    </EmbedModeContext.Provider>
  );
};
